import React, { useEffect, useState } from "react";
import axios from "axios";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth.jsx";
import { PRODUCTS } from "../lib/products.js";
import { Toaster } from "../components/ui/sonner";
import { toast } from "sonner";
import AdminUsers from "./AdminUsers.jsx";
import AdminInbox from "./AdminInbox.jsx";

const LOGO = "https://i.imgur.com/27IUlf9.png";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTS = [
  { v: "new", label: "Nueva", cls: "bg-[#b45f3e]/15 text-[#b45f3e]" },
  { v: "contacted", label: "Contactado", cls: "bg-[#9ead9b]/30 text-[#133322]" },
  { v: "closed", label: "Cerrada", cls: "bg-[#e5e8e2] text-[#5c6b61]" },
];

function QuotesTab({ token, isAdmin }) {
  const [quotes, setQuotes] = useState([]);
  const [filter, setFilter] = useState("all");

  const fetchQuotes = async () => {
    try {
      const res = await axios.get(`${API}/admin/quotes`, { headers: { Authorization: `Bearer ${token}` } });
      setQuotes(res.data.quotes || []);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { if (token) fetchQuotes(); }, [token]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/quotes/${id}`, { status }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Estado actualizado");
      fetchQuotes();
    } catch { toast.error("Solo admins pueden cambiar el estado"); }
  };

  const productName = (id) => PRODUCTS.find(p => p.id === id)?.name.es || id || "—";
  const filtered = filter === "all" ? quotes : quotes.filter(q => (q.status || "new") === filter);

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setFilter("all")} className={`text-xs rounded-full px-4 py-2 ${filter==="all"?"bg-[#133322] text-[#f7f7f4]":"bg-white border border-[#e5e8e2]"}`} data-testid="filter-all">
            Todas ({quotes.length})
          </button>
          {STATUS_OPTS.map(s => (
            <button key={s.v} onClick={() => setFilter(s.v)} className={`text-xs rounded-full px-4 py-2 ${filter===s.v?"bg-[#133322] text-[#f7f7f4]":"bg-white border border-[#e5e8e2]"}`} data-testid={`filter-${s.v}`}>
              {s.label} ({quotes.filter(q => (q.status||"new")===s.v).length})
            </button>
          ))}
        </div>
        <button onClick={fetchQuotes} className="text-sm text-[#133322] hover:underline" data-testid="refresh-btn">
          <i className="fa-solid fa-rotate"></i> Actualizar
        </button>
      </div>

      <div className="eu-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#f7f7f4] border-b border-[#e5e8e2]">
              <tr className="text-left">
                <th className="p-4 eu-data-label">Fecha</th>
                <th className="p-4 eu-data-label">Nombre</th>
                <th className="p-4 eu-data-label">Contacto</th>
                <th className="p-4 eu-data-label">Producto</th>
                <th className="p-4 eu-data-label">Mensaje</th>
                <th className="p-4 eu-data-label">Estado</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (<tr><td colSpan="6" className="p-12 text-center text-[#5c6b61]">Sin cotizaciones</td></tr>)}
              {filtered.map(q => {
                const cur = STATUS_OPTS.find(s => s.v === (q.status||"new"));
                return (
                  <tr key={q.id} className="border-b border-[#e5e8e2] hover:bg-[#f7f7f4]/60" data-testid={`quote-row-${q.id}`}>
                    <td className="p-4 text-xs text-[#5c6b61] font-mono-eu whitespace-nowrap">{new Date(q.created_at).toLocaleString("es-ES")}</td>
                    <td className="p-4">
                      <div className="font-medium text-[#133322]">{q.name}</div>
                      <div className="text-xs text-[#5c6b61]">{q.company || "—"} · {q.country || "—"}</div>
                    </td>
                    <td className="p-4 text-xs">
                      <div>{q.email}</div>
                      <div className="text-[#5c6b61]">{q.phone || "—"}</div>
                    </td>
                    <td className="p-4 text-xs">
                      <div className="font-medium text-[#133322]">{productName(q.product_id)}</div>
                      <div className="text-[#5c6b61]">{q.quantity || "—"}</div>
                    </td>
                    <td className="p-4 max-w-xs text-xs text-[#1c1c1c]">{q.message || "—"}</td>
                    <td className="p-4">
                      <select disabled={!isAdmin} value={q.status||"new"} onChange={(e) => updateStatus(q.id, e.target.value)} className={`text-xs rounded-full px-3 py-1.5 border-0 outline-none disabled:opacity-60 ${cur?.cls||""}`} data-testid={`status-${q.id}`}>
                        {STATUS_OPTS.map(s => <option key={s.v} value={s.v}>{s.label}</option>)}
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function ChannelsTab({ token }) {
  const [msgs, setMsgs] = useState([]);
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/admin/channel-messages`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setMsgs(r.data.messages || [])).catch(()=>{});
  }, [token]);

  const ICONS = { whatsapp: "fa-brands fa-whatsapp text-[#25D366]", instagram: "fa-brands fa-instagram text-[#E4405F]", facebook: "fa-brands fa-facebook text-[#1877F2]" };

  return (
    <div className="eu-card p-6" data-testid="channels-tab">
      <div className="eu-data-label mb-4">FLORA MULTICANAL · ÚLTIMAS CONVERSACIONES</div>
      {msgs.length === 0 ? (
        <div className="text-center py-12 text-[#5c6b61] text-sm">
          Sin mensajes aún. Configura WhatsApp / Instagram / Facebook en Meta Business y apunta los webhooks a:<br/>
          <code className="text-xs bg-[#f7f7f4] px-2 py-1 rounded mt-2 inline-block font-mono-eu">{process.env.REACT_APP_BACKEND_URL}/api/webhooks/meta</code>
        </div>
      ) : (
        <div className="space-y-3">
          {msgs.map((m, i) => (
            <div key={i} className="border border-[#e5e8e2] rounded-xl p-4">
              <div className="flex items-center gap-2 text-xs">
                <i className={ICONS[m.channel] || "fa-solid fa-message"}></i>
                <span className="font-mono-eu uppercase">{m.channel}</span>
                <span className="text-[#5c6b61]">· {new Date(m.created_at).toLocaleString("es-ES")}</span>
                <span className="text-[#5c6b61]">· De: {m.from}</span>
              </div>
              <div className="mt-3 text-sm bg-[#f7f7f4] rounded-lg p-3"><strong>Usuario:</strong> {m.text}</div>
              <div className="mt-2 text-sm bg-[#133322]/5 rounded-lg p-3"><strong className="text-[#133322]">Flora:</strong> {m.reply}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const { user, loading, token, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("quotes");

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#f7f7f4]">Cargando…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  const isAdmin = user.role === "admin";
  const tabs = [
    { id: "quotes", label: "Cotizaciones", icon: "fa-paper-plane" },
    { id: "inbox", label: "Bandeja Flora", icon: "fa-inbox" },
    { id: "channels", label: "Log canales", icon: "fa-list" },
    ...(isAdmin ? [{ id: "users", label: "Usuarios", icon: "fa-users" }] : []),
  ];

  return (
    <div className="min-h-screen bg-[#f7f7f4]">
      <header className="eu-nav-blur sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO} alt="" className="h-8" />
            <div>
              <div className="eu-data-label">PANEL ADMIN</div>
              <div className="font-display text-xl text-[#133322]">EUZERONE</div>
            </div>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <span className="text-[#5c6b61]">
              {user.email}
              <span className={`ml-2 text-[10px] px-2 py-0.5 rounded-full ${user.role === "admin" ? "bg-[#133322] text-[#f7f7f4]" : "bg-[#9ead9b]/30 text-[#133322]"}`}>{user.role}</span>
            </span>
            <button onClick={() => { logout(); nav("/admin/login"); }} className="eu-btn-outline text-sm py-2 px-4" data-testid="admin-logout">
              Salir
            </button>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 pb-3 flex gap-1 overflow-x-auto">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
              className={`text-sm px-4 py-2 rounded-full transition whitespace-nowrap ${tab===t.id ? "bg-[#133322] text-[#f7f7f4]" : "text-[#5c6b61] hover:text-[#133322]"}`}>
              <i className={`fa-solid ${t.icon} text-xs mr-2`}></i>{t.label}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        {tab === "quotes" && <QuotesTab token={token} isAdmin={isAdmin} />}
        {tab === "inbox" && <AdminInbox />}
        {tab === "channels" && <ChannelsTab token={token} />}
        {tab === "users" && isAdmin && <AdminUsers />}
      </main>
      <Toaster richColors position="top-center" />
    </div>
  );
}
